import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import AnalyticsService from '../../services/AnalyticsService';
import { viewerSeesRevenue } from '../../utils/analyticsAccess';
import { formatDate, formatMoney, formatNumber } from '../../utils/analyticsFormat';
import ChannelTagsField from './ChannelTagsField';

function apiError(error, fallback) {
  return error.response?.data?.error || fallback;
}

function toPercent(share) {
  return String(Math.round(share * 10000) / 100);
}

function fromPercent(text) {
  return Number(text) / 100;
}

function validPercent(text) {
  const value = Number(text);
  return text !== '' && Number.isFinite(value) && value >= 0 && value <= 100;
}

function PercentField({ label, value, onChange, helperText, sx }) {
  const valid = validPercent(value);
  return (
    <TextField
      label={label}
      type="number"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={!valid}
      helperText={valid ? helperText : 'Enter a number from 0 to 100.'}
      inputProps={{ min: 0, max: 100, step: 1 }}
      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
      sx={sx}
    />
  );
}

function validRpm(text) {
  const value = Number(text);
  return text === '' || (Number.isFinite(value) && value > 0 && value <= 1000);
}

// Blank RPM turns the estimate off.
function estimateFrom(rpmText, from) {
  return rpmText === '' ? null : { rpm: Number(rpmText), from };
}

function sameEstimate(a, b) {
  return a === b || (a && b && a.rpm === b.rpm && a.from === b.from);
}

function sameTags(a, b) {
  return a.length === b.length && a.every((tag, index) => tag === b[index]);
}

/**
 * The channel's folder and tags, the partner's share of its revenue, and, for
 * connected channels whose revenue YouTube does not report, an RPM to estimate
 * it at. Users who do not see revenue only choose the folder and tags.
 */
export function ChannelSettingsDialog({ channel, folders, existingTags, onClose, onSaved }) {
  const showRevenue = viewerSeesRevenue();
  const [folderId, setFolderId] = useState(channel.folderId || '');
  const [tags, setTags] = useState(channel.tags || []);
  const [share, setShare] = useState(showRevenue ? toPercent(channel.revenueShare) : '100');
  const [estimatedRpm, setEstimatedRpm] = useState(
    channel.revenueEstimate ? String(channel.revenueEstimate.rpm) : '',
  );
  const [estimateStart, setEstimateStart] = useState(channel.revenueEstimate?.from || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canEstimate =
    showRevenue && channel.source === 'youtube' && channel.revenueAccess !== 'granted';
  const estimateValid = validRpm(estimatedRpm) && (estimatedRpm === '' || Boolean(estimateStart));

  async function save() {
    setSaving(true);
    setError('');
    try {
      if ((channel.folderId || '') !== folderId)
        await AnalyticsService.moveToFolder(channel.id, folderId || null);
      const changes = {};
      if (!sameTags(tags, channel.tags || [])) changes.tags = tags;
      if (showRevenue && fromPercent(share) !== channel.revenueShare)
        changes.revenueShare = fromPercent(share);
      const estimate = estimateFrom(estimatedRpm, estimateStart);
      if (canEstimate && !sameEstimate(estimate, channel.revenueEstimate))
        changes.revenueEstimate = estimate;
      if (Object.keys(changes).length) await AnalyticsService.updateChannel(channel.id, changes);
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Could not save the channel.'));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{channel.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            label="Folder"
            value={folderId}
            onChange={(event) => setFolderId(event.target.value)}
            helperText={folders.length ? null : 'Create folders in the Manage tab.'}
          >
            <MenuItem value="">Ungrouped</MenuItem>
            {folders.map((folder) => (
              <MenuItem key={folder.id} value={folder.id}>
                {folder.name}
              </MenuItem>
            ))}
          </TextField>
          <ChannelTagsField value={tags} onChange={setTags} existingTags={existingTags} />
          {showRevenue && (
            <PercentField
              label="Your revenue share"
              value={share}
              onChange={setShare}
              helperText="If you split this channel's revenue, your cut. Totals still show the full amount."
            />
          )}
          {canEstimate && (
            <>
              <TextField
                label="Estimated RPM"
                type="number"
                value={estimatedRpm}
                onChange={(event) => setEstimatedRpm(event.target.value)}
                error={!validRpm(estimatedRpm)}
                helperText={
                  validRpm(estimatedRpm)
                    ? "YouTube does not report this channel's revenue. Estimate it from views at this RPM, or leave blank."
                    : 'Enter an RPM above $0 and at most $1,000.'
                }
                inputProps={{ min: 0, max: 1000, step: 0.01 }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              {estimatedRpm !== '' && (
                <TextField
                  label="Estimate revenue from"
                  type="date"
                  value={estimateStart}
                  onChange={(event) => setEstimateStart(event.target.value)}
                  error={!estimateStart}
                  helperText="The first monetized day. Earlier days have no revenue."
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !validPercent(share) || !estimateValid}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Adds a channel from a YouTube Studio export, or uploads a newer export for
 * an imported channel when `channel` is given.
 */
export function ImportDialog({ channel, onClose, onImported }) {
  const [name, setName] = useState('');
  const [youtubeChannelId, setYoutubeChannelId] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [createdId, setCreatedId] = useState(null);

  async function submit() {
    setSaving(true);
    setError('');
    try {
      let id = channel?.id || createdId;
      if (!id) {
        id = (await AnalyticsService.createImportChannel(name.trim(), youtubeChannelId.trim())).id;
        setCreatedId(id);
      }
      setResult(await AnalyticsService.importExport(id, file));
      onImported();
    } catch (err) {
      setError(apiError(err, 'Could not import the export.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{channel ? `Upload new export · ${channel.name}` : 'Add a channel from YouTube Studio'}</DialogTitle>
      <DialogContent>
        {result ? (
          <Alert severity="success">
            Imported {formatNumber(result.days)} days ({formatDate(result.from)} –{' '}
            {formatDate(result.to)}): {formatNumber(result.views)} views and{' '}
            {formatMoney(result.revenue)} revenue. Daily revenue is estimated from each day's
            views.
          </Alert>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <DialogContentText>
              For channels you reach through a CMS or manager access. In YouTube Studio, open{' '}
              <strong>Analytics → Advanced mode → Content</strong>, choose a date range, add the{' '}
              <strong>Estimated revenue</strong> metric, and export as CSV. Upload the ZIP you get.
            </DialogContentText>
            {!channel && !createdId && (
              <>
                <TextField
                  label="Channel name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  autoFocus
                />
                <TextField
                  label="YouTube channel id (optional)"
                  value={youtubeChannelId}
                  onChange={(event) => setYoutubeChannelId(event.target.value)}
                  placeholder="UC…"
                  helperText="Starts with UC. Shown for reference only."
                />
              </>
            )}
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ alignSelf: 'flex-start', boxShadow: 'none' }}
            >
              {file ? file.name : 'Choose ZIP file'}
              <input
                hidden
                type="file"
                accept=".zip,application/zip"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </Button>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || !file || (!channel && !createdId && !name.trim())}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Import export
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function RemoveChannelDialog({ channel, onClose, onRemoved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function remove() {
    setSaving(true);
    try {
      await AnalyticsService.removeChannel(channel.id);
      onRemoved();
    } catch (err) {
      setError(apiError(err, 'Could not remove the channel.'));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove {channel.name}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {channel.uploadConnectionKind
            ? "This permanently deletes the channel's stored analytics (daily and video numbers). Its upload channel stays connected to YouTube. It cannot be undone."
            : "This permanently deletes the channel's stored analytics (daily and video numbers) and its YouTube connection. It cannot be undone."}
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" onClick={remove} disabled={saving}>
          Remove channel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
